import React from 'react'
import { Box, type BoxProps } from '@chakra-ui/react'

import {
    resolveMascotEarStyle,
    resolveMascotSnout,
    type ClinicMascotIdentity,
    type MascotSpecies,
} from './mascot-types'
import CatMascot from './species/cat'
import HorseMascot from './species/horse'
import RegularDogMascot from './species/regular-dog'
import RabbitMascot from './species/rabbit'
import type { MascotPose, MascotSvgProps } from './species/mascot-svg-shared'

const SPECIES_COMPONENT: Record<
    MascotSpecies,
    React.ComponentType<MascotSvgProps>
> = {
    regular_dog: RegularDogMascot,
    cat: CatMascot,
    horse: HorseMascot,
    rabbit: RabbitMascot,
}

export type MascotAvatarProps = {
    mascot: ClinicMascotIdentity
    size?: number
    /**
     * `peek` crops the face at a fence line and hangs paws over it. Position
     * it against a real UI edge (see `MASCOT_PEEK_LEDGE_RATIO`) — it does not
     * draw a fence of its own.
     */
    pose?: MascotPose
    /** Show the configured name / nickname under the face. */
    showCaption?: boolean
} & Omit<BoxProps, 'children' | 'size'>

/**
 * Renders the clinic's tintable mascot SVG from identity data.
 * Idle = still (no animation) by design.
 */
export default function MascotAvatar({
    mascot,
    size = 40,
    pose = 'portrait',
    showCaption = false,
    ...boxProps
}: MascotAvatarProps) {
    const Species = SPECIES_COMPONENT[mascot.species]
    const title =
        mascot.name.trim() || mascot.nickname.trim() || 'Clinic mascot'

    return (
        <Box
            display="inline-flex"
            flexDirection="column"
            alignItems="center"
            lineHeight={1}
            {...boxProps}
        >
            <Species
                size={size}
                primaryColor={mascot.primaryColor}
                secondaryColor={mascot.secondaryColor}
                eyeColor={mascot.eyeColor}
                pattern={mascot.pattern}
                furType={mascot.furType}
                snout={resolveMascotSnout(mascot.species, mascot.snout)}
                earStyle={resolveMascotEarStyle(
                    mascot.species,
                    mascot.earStyle,
                )}
                pose={pose}
                title={title}
            />
            {showCaption && (mascot.name.trim() || mascot.nickname.trim()) ? (
                <Box
                    as="span"
                    mt={1}
                    fontSize="xs"
                    fontWeight="medium"
                    color="gray.600"
                    noOfLines={1}
                    maxW={`${Math.max(size, 64)}px`}
                    textAlign="center"
                >
                    {mascot.name.trim() || mascot.nickname.trim()}
                </Box>
            ) : null}
        </Box>
    )
}
